import type { Model, PixelSize, Request } from './contracts';

export const availableDurations = (model: Model | undefined, quality: string) => model?.durations.filter(value =>
  (model.durationMaxWhen || []).every(rule => rule.key !== 'quality' || rule.value !== quality || parseFloat(value) <= rule.max)) || [];

export function fitCustomSize(size: PixelSize, limits: Model['customSizeLimits']): PixelSize {
  if (!Number.isInteger(size.width) || !Number.isInteger(size.height) || size.width <= 0 || size.height <= 0) throw Error('Enter a positive width and height in pixels.');
  if (!limits) return size;
  let { width: w, height: h } = size;
  const { minPixels, maxPixels, maxEdge, multiple: m } = limits;
  if (Math.max(w, h) > maxEdge) { const scale = maxEdge / Math.max(w, h); w *= scale; h *= scale; }
  if (w * h > maxPixels) { const scale = Math.sqrt(maxPixels / (w * h)); w *= scale; h *= scale; }
  w = Math.max(m, Math.floor(w / m) * m); h = Math.max(m, Math.floor(h / m) * m);
  if (w * h < minPixels) { const scale = Math.sqrt(minPixels / (w * h)); w = Math.ceil(w * scale / m) * m; h = Math.ceil(h * scale / m) * m; }
  if (w > maxEdge || h > maxEdge || w * h > maxPixels) throw Error('That shape is outside this model’s custom-size range. Choose less extreme dimensions.');
  return { width: w, height: h };
}

/** "4:5" as a number; null for anything that is not two numbers ("auto"). */
export const ratioOf = (value: string) => {
  const match = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/.exec(value);
  return match && Number(match[2]) > 0 ? Number(match[1]) / Number(match[2]) : null;
};

/** The offered ratio nearest a shape. Compared as logs, so twice as wide is as far as twice as tall. */
export function closestRatio(size: PixelSize, ratios: readonly string[]): string | null {
  const distance = (ratio: number) => Math.abs(Math.log(ratio * size.height / size.width));
  return ratios.reduce<string | null>((best, value) => {
    const ratio = ratioOf(value);
    return ratio && (best === null || distance(ratio) < distance(ratioOf(best)!)) ? value : best;
  }, null);
}

/** What a reference asks of the size controls: its closest ratio, and its own size when the model takes one. */
export function sizeForReference(model: Model, size: PixelSize): { aspect: string | null; custom: PixelSize | null } {
  const whole = { width: Math.round(size.width), height: Math.round(size.height) };
  let custom: PixelSize | null = null;
  if (model.customSize) { try { custom = fitCustomSize(whole, model.customSizeLimits); } catch { /* A shape past the model's range keeps the closest ratio. */ } }
  return { aspect: closestRatio(size, model.ratios), custom };
}

/** One settings object feeds the quote and generation. Never derives size from the box. */
export function modelInputs(model: Model, picks: { aspect: string; custom: PixelSize | null; quality: string; duration: string; mode: string }): Request['inputs'] {
  const inputs: Request['inputs'] = {};
  if (picks.custom) {
    if (!model.customSize) throw Error('This model does not support a custom size. Choose an aspect ratio.');
    inputs.customSize = fitCustomSize(picks.custom, model.customSizeLimits);
  } else if (model.ratios.length) {
    if (!model.ratios.includes(picks.aspect)) throw Error('Choose an aspect ratio supported by this model.');
    inputs.aspectRatio = picks.aspect;
  }
  for (const [key, values, picked] of [['quality', model.qualities, picks.quality], ['duration', model.durations, picks.duration], ['mode', model.modes, picks.mode]] as const) {
    if (values.length) inputs[key] = values.includes(picked) ? picked : values[0];
  }
  return inputs;
}
